import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Slider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

const AdvancedChartOptions = ({
  chartType = 'line',
  onOptionsChange,
  onRefresh,
  onExport,
  onFullscreen,
  options = {},
  showExportOptions = true,
  showFullscreenOption = true
}) => {
  const [localOptions, setLocalOptions] = useState({
    aggregationMethod: 'sum',
    timeRange: '1year',
    groupBy: 'month',
    showTrendLine: false,
    showDataLabels: false,
    chartStyle: 'modern',
    colorScheme: 'default',
    animation: true,
    gridLines: true,
    opacity: 0.8,
    strokeWidth: 2,
    ...options
  });

  const handleOptionChange = (key, value) => {
    const newOptions = { ...localOptions, [key]: value };
    setLocalOptions(newOptions);
    onOptionsChange?.(newOptions);
  };

  const aggregationMethods = [
    { value: 'sum', label: 'Sum' },
    { value: 'average', label: 'Average' },
    { value: 'count', label: 'Count' },
    { value: 'median', label: 'Median' },
    { value: 'max', label: 'Maximum' },
    { value: 'min', label: 'Minimum' }
  ];

  const timeRanges = [
    { value: '1month', label: 'Last Month' },
    { value: '3months', label: 'Last 3 Months' },
    { value: '6months', label: 'Last 6 Months' },
    { value: '1year', label: 'Last Year' },
    { value: '2years', label: 'Last 2 Years' },
    { value: 'all', label: 'All Time' }
  ];

  const groupByOptions = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' },
    { value: 'year', label: 'Yearly' }
  ];

  const colorSchemes = [
    { value: 'default', label: 'Default' },
    { value: 'viridis', label: 'Viridis' },
    { value: 'plasma', label: 'Plasma' },
    { value: 'warm', label: 'Warm' },
    { value: 'cool', label: 'Cool' },
    { value: 'categorical', label: 'Categorical' }
  ];

  const chartStyles = [
    { value: 'modern', label: 'Modern' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'classic', label: 'Classic' },
    { value: 'bold', label: 'Bold' }
  ];

  return (
    <Paper elevation={1} sx={{ mb: 2 }}>
      <Box sx={{ p: 2 }}>
        {/* Header with actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon />
            Chart Options
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh data">
              <IconButton size="small" onClick={onRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {showExportOptions && (
              <Tooltip title="Export chart">
                <IconButton size="small" onClick={onExport}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
            {showFullscreenOption && (
              <Tooltip title="Fullscreen view">
                <IconButton size="small" onClick={onFullscreen}>
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* Quick presets */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Quick Presets
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label="Last 30 Days"
              variant="outlined"
              size="small"
              onClick={() => {
                handleOptionChange('timeRange', '1month');
                handleOptionChange('groupBy', 'day');
              }}
            />
            <Chip
              label="Monthly Trends"
              variant="outlined"
              size="small"
              onClick={() => {
                handleOptionChange('timeRange', '1year');
                handleOptionChange('groupBy', 'month');
                handleOptionChange('showTrendLine', true);
              }}
            />
            <Chip
              label="Yearly Overview"
              variant="outlined"
              size="small"
              onClick={() => {
                handleOptionChange('timeRange', 'all');
                handleOptionChange('groupBy', 'year');
                handleOptionChange('aggregationMethod', 'sum');
              }}
            />
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Data Options */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Data Options</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Aggregation Method</InputLabel>
                <Select
                  value={localOptions.aggregationMethod}
                  label="Aggregation Method"
                  onChange={(e) => handleOptionChange('aggregationMethod', e.target.value)}
                >
                  {aggregationMethods.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={localOptions.timeRange}
                  label="Time Range"
                  onChange={(e) => handleOptionChange('timeRange', e.target.value)}
                >
                  {timeRanges.map((range) => (
                    <MenuItem key={range.value} value={range.value}>
                      {range.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Group By</InputLabel>
                <Select
                  value={localOptions.groupBy}
                  label="Group By"
                  onChange={(e) => handleOptionChange('groupBy', e.target.value)}
                >
                  {groupByOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Visual Options */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Visual Options</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Color Scheme</InputLabel>
                <Select
                  value={localOptions.colorScheme}
                  label="Color Scheme"
                  onChange={(e) => handleOptionChange('colorScheme', e.target.value)}
                >
                  {colorSchemes.map((scheme) => (
                    <MenuItem key={scheme.value} value={scheme.value}>
                      {scheme.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Chart Style</InputLabel>
                <Select
                  value={localOptions.chartStyle}
                  label="Chart Style"
                  onChange={(e) => handleOptionChange('chartStyle', e.target.value)}
                >
                  {chartStyles.map((style) => (
                    <MenuItem key={style.value} value={style.value}>
                      {style.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={localOptions.showTrendLine}
                    onChange={(e) => handleOptionChange('showTrendLine', e.target.checked)}
                    size="small"
                  />
                }
                label="Show Trend Line"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={localOptions.showDataLabels}
                    onChange={(e) => handleOptionChange('showDataLabels', e.target.checked)}
                    size="small"
                  />
                }
                label="Show Data Labels"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={localOptions.animation}
                    onChange={(e) => handleOptionChange('animation', e.target.checked)}
                    size="small"
                  />
                }
                label="Enable Animations"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={localOptions.gridLines}
                    onChange={(e) => handleOptionChange('gridLines', e.target.checked)}
                    size="small"
                  />
                }
                label="Show Grid Lines"
              />

              <Box>
                <Typography variant="caption" gutterBottom>
                  Opacity: {Math.round(localOptions.opacity * 100)}%
                </Typography>
                <Slider
                  value={localOptions.opacity}
                  onChange={(e, value) => handleOptionChange('opacity', value)}
                  min={0.1}
                  max={1}
                  step={0.1}
                  size="small"
                />
              </Box>

              {chartType === 'line' && (
                <Box>
                  <Typography variant="caption" gutterBottom>
                    Stroke Width: {localOptions.strokeWidth}px
                  </Typography>
                  <Slider
                    value={localOptions.strokeWidth}
                    onChange={(e, value) => handleOptionChange('strokeWidth', value)}
                    min={1}
                    max={5}
                    step={0.5}
                    size="small"
                  />
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Paper>
  );
};

export default AdvancedChartOptions;
